// #region Global Imports
import * as React from 'react';
import type { DateFormatter } from 'react-day-picker';
import { DayPicker, useInput } from 'react-day-picker';
import { Popover } from '@headlessui/react';
import merge from 'lodash/merge';
import de from 'date-fns/locale/de';
import frCH from 'date-fns/locale/fr-CH';
import { format, parse } from 'date-fns';
// #endregion Global Imports
// #region Local Imports
import { usePopper } from 'react-popper';
import styles from './styles.module.sass';

interface DayPickerInputProps {
  selected?: string;
  onChange: (day?: Date) => void;
  fromYear?: number;
  toYear?: number;
  locale?: string;
}

// #endregion Local Imports
export const DayPickerInput: React.FunctionComponent<DayPickerInputProps> = ({
  onChange,
  fromYear = 1990,
  toYear = new Date().getFullYear(),
  locale = 'de',
  selected,
}) => {
  const { inputProps, dayPickerProps } = useInput({
    fromYear,
    toYear,
    format: 'dd.MM.yyyy',
    required: true,
  });

  const [referenceElement, setReferenceElement] = React.useState();
  const [popperElement, setPopperElement] = React.useState();
  const { styles: s, attributes } = usePopper(referenceElement, popperElement);

  let parsedDate: Date | undefined;

  if (selected != null) {
    parsedDate = parse(selected, 'yyyy-MM-dd', new Date());
  }

  const formatCaption: DateFormatter = (month, _) => {
    return String(month.getMonth() + 1)
      .concat(' · ')
      .concat(String(month.getFullYear()));
  };

  return (
    <Popover>
      {({ open, close }) => (
        <>
          <Popover.Button as="div">
            <label>
              <input
                // @ts-expect-error
                ref={setReferenceElement}
                {...merge(inputProps, { placeholder: 'DD.MM.YYYY' })}
                value={parsedDate ? format(parsedDate, 'dd.MM.yyyy') : ''}
              />
            </label>
          </Popover.Button>
          {open ? (
            <Popover.Panel
              className={styles.DayPicker__input_active}
              // @ts-expect-error
              ref={setPopperElement}
              style={merge(s.propper, {
                width: 'max-content',
                zIndex: 201,
                position: 'absolute',
                marginTop: '.33em',
              })}
              {...attributes.popper}
            >
              <DayPicker
                {...dayPickerProps}
                captionLayout="dropdown"
                fixedWeeks
                formatters={{ formatCaption }}
                locale={locale === 'de' ? de : frCH}
                mode="single"
                onDayClick={(day) => {
                  if(selected != format(day, 'yyyy-MM-dd')) {
                    onChange(day)
                  } else {
                    onChange(undefined);
                  }

                  close()
                }}
                selected={parsedDate ?? undefined}
                showOutsideDays
              />
            </Popover.Panel>
          ) : null}
        </>
      )}
    </Popover>
  );
};
